import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Account } from '@shared/contracts/entities';
import {
  getAccounts,
  transferBetweenAccounts,
  validateTransfer,
  type TransferResult,
} from '@/mocks/api';
import { useAsync, useMutation } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { newIdempotencyKey } from '@/services/idempotency';
import { nowIso } from '@/services/clock';
import { formatDateTime } from '@/services/formatters';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import {
  Amount,
  DetailList,
  ListRow,
  ResultMedallion,
  SectionHeader,
  StatusBadge,
} from '@/components/banking';
import { ErrorState, LoadingRegion, Notice, RowSkeleton } from '@/components/feedback';
import { isMockApiError } from '@/mocks/errors';

/**
 * Account-to-account transfer.
 *
 * Implements the package's account-to-account workflow: source, destination,
 * amount, balance validation, review, confirmation, and failure/recovery.
 *
 * This was previously a dead entry point — the home tile linked to
 * `?mode=transfer` and nothing read the parameter, so the action silently
 * behaved like an ordinary payment.
 *
 * No SCA step: moving money between the company's own accounts does not leave
 * the customer's control, so it carries a review and confirmation rather than a
 * strong-authentication challenge.
 */

type Step = 'source' | 'destination' | 'details' | 'review' | 'result';

const TITLE: Record<Step, string> = {
  source: 'Move from',
  destination: 'Move to',
  details: 'How much',
  review: 'Review transfer',
  result: 'Transfer result',
};

export function TransferFlow() {
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const [step, setStep] = useState<Step>('source');
  const [sourceId, setSourceId] = useState('');
  const [destinationId, setDestinationId] = useState('');
  const [amountText, setAmountText] = useState('');
  const [reference, setReference] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [warnings, setWarnings] = useState<readonly string[]>([]);
  const [result, setResult] = useState<TransferResult | null>(null);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const idempotencyKey = useRef(newIdempotencyKey('tfr'));
  const amountRef = useRef<HTMLInputElement | null>(null);

  const accounts = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getAccounts(companyId, userId)).data;
  }, [companyId, userId]);

  const submitMutation = useMutation(async () => {
    if (!companyId || !userId) throw new Error('no session');
    return (
      await transferBetweenAccounts(
        companyId,
        userId,
        {
          sourceAccountId: sourceId,
          destinationAccountId: destinationId,
          amount: amountMinor,
          reference,
        },
        idempotencyKey.current,
      )
    ).data;
  });

  const list = accounts.data ?? [];
  const from = list.find((a) => a.id === sourceId);
  const to = list.find((a) => a.id === destinationId);
  const amountMinor = Math.round(Number(amountText.replace(/,/g, '')) * 100);

  if (!resolved) return null;

  const canSendFrom = (a: Account) => a.status === 'active';
  const canReceive = (a: Account) => a.status !== 'closed' && a.status !== 'suspended';

  const check = async (): Promise<boolean> => {
    if (!companyId || !userId) return false;
    const v = (
      await validateTransfer(companyId, userId, {
        sourceAccountId: sourceId,
        destinationAccountId: destinationId,
        amount: Number.isFinite(amountMinor) ? amountMinor : 0,
        reference,
      })
    ).data;
    setFieldErrors({ ...v.fieldErrors });
    setWarnings(v.warnings);
    if (!v.valid && v.fieldErrors['amount']) amountRef.current?.focus();
    return v.valid;
  };

  return (
    <>
      <MobileHeader
        title={TITLE[step]}
        backTo={step === 'result' ? '/mobile/home' : '/mobile/payments/new'}
        backLabel={step === 'result' ? 'Home' : 'Send money'}
      />

      {step !== 'result' ? (
        <p className="tm-hint" role="status" style={{ marginBlockEnd: 8 }}>
          Moving money between your own accounts
        </p>
      ) : null}

      {/* ---------------------------------------------------- source --- */}
      {step === 'source' ? (
        <div className="tm-stack">
          {accounts.status === 'loading' ? (
            <LoadingRegion label="Loading accounts">
              <div className="tm-card tm-card--flush">
                <RowSkeleton count={3} />
              </div>
            </LoadingRegion>
          ) : accounts.status === 'error' ? (
            <ErrorState error={accounts.error} onRetry={accounts.reload} />
          ) : (
            <div className="tm-card tm-card--flush">
              <ul className="tm-rows">
                {list.map((a) => (
                  <li key={a.id}>
                    <ListRow
                      {...(canSendFrom(a)
                        ? {
                            onClick: () => {
                              setSourceId(a.id);
                              setStep('destination');
                            },
                          }
                        : {})}
                      title={a.displayName}
                      meta={
                        canSendFrom(a)
                          ? `${a.currency} available`
                          : (a.statusReason ?? 'Cannot move money out of this account')
                      }
                      end={
                        <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                          <Amount minorUnits={a.availableBalance} currency={a.currency} />
                          {!canSendFrom(a) ? <StatusBadge status={a.status} /> : null}
                        </span>
                      }
                      ariaLabel={`Move money from ${a.displayName}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}

      {/* ----------------------------------------------- destination --- */}
      {step === 'destination' ? (
        <div className="tm-stack">
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              {list
                .filter((a) => a.id !== sourceId)
                .map((a) => {
                  const sameCurrency = !from || a.currency === from.currency;
                  const usable = canReceive(a) && sameCurrency;
                  return (
                    <li key={a.id}>
                      <ListRow
                        {...(usable
                          ? {
                              onClick: () => {
                                setDestinationId(a.id);
                                setStep('details');
                              },
                            }
                          : {})}
                        title={a.displayName}
                        meta={
                          !sameCurrency
                            ? `Holds ${a.currency} — use Exchange for a currency conversion`
                            : usable
                              ? `${a.currency} account`
                              : (a.statusReason ?? 'Cannot receive money')
                        }
                        end={<Amount minorUnits={a.availableBalance} currency={a.currency} />}
                        ariaLabel={`Move money to ${a.displayName}`}
                      />
                    </li>
                  );
                })}
            </ul>
          </div>
          <p className="tm-hint">
            Moving between different currencies is a currency exchange, not a transfer.
          </p>
          <Link to="/mobile/fx" className="tm-btn tm-btn--secondary tm-btn--block">
            Exchange currency instead
          </Link>
        </div>
      ) : null}

      {/* --------------------------------------------------- details --- */}
      {step === 'details' && from && to ? (
        <form
          className="tm-stack"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void check().then((ok) => {
              if (ok) setStep('review');
            });
          }}
        >
          <div className="tm-card">
            <p className="tm-eyebrow">Moving</p>
            <strong>
              {from.displayName} → {to.displayName}
            </strong>
            <p className="tm-hint" style={{ margin: '2px 0 0' }}>
              <Amount minorUnits={from.availableBalance} currency={from.currency} /> available
            </p>
          </div>

          <div className="tm-field">
            <label className="tm-label" htmlFor="tfr-amount">
              Amount ({from.currency})
            </label>
            <input
              id="tfr-amount"
              ref={amountRef}
              className="tm-input tm-input--amount"
              inputMode="decimal"
              value={amountText}
              onChange={(e) => setAmountText(e.target.value.replace(/[^\d.,]/g, ''))}
              aria-invalid={fieldErrors['amount'] ? true : undefined}
              aria-describedby={fieldErrors['amount'] ? 'tfr-amount-error' : undefined}
            />
            <div className="tm-field__error-slot">
              {fieldErrors['amount'] ? (
                <span className="tm-field__error" id="tfr-amount-error">
                  {fieldErrors['amount']}
                </span>
              ) : null}
            </div>
          </div>

          <div className="tm-field">
            <label className="tm-label" htmlFor="tfr-reference">
              Reference (optional)
            </label>
            <input
              id="tfr-reference"
              className="tm-input"
              maxLength={18}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
            <div className="tm-field__error-slot" />
          </div>

          <button type="submit" className="tm-btn tm-btn--block">
            Continue to review
          </button>
        </form>
      ) : null}

      {/* ---------------------------------------------------- review --- */}
      {step === 'review' && from && to ? (
        <div className="tm-stack">
          <div className="tm-card" style={{ textAlign: 'center' }}>
            <p className="tm-eyebrow">You are moving</p>
            <Amount minorUnits={amountMinor} currency={from.currency} size="hero" />
            <p className="tm-muted" style={{ margin: '6px 0 0' }}>
              {from.displayName} → {to.displayName}
            </p>
          </div>

          {submitError ? (
            <Notice tone="danger" title="The transfer was not made">
              {submitError}
            </Notice>
          ) : null}

          {warnings.map((w) => (
            <Notice key={w} tone="warning" title="Check before you move this">
              {w}
            </Notice>
          ))}

          <div className="tm-card">
            <DetailList
              items={[
                { label: 'From', value: from.displayName },
                { label: 'To', value: to.displayName },
                {
                  label: 'Amount',
                  value: <Amount minorUnits={amountMinor} currency={from.currency} />,
                },
                { label: 'Reference', value: reference || 'Transfer' },
                { label: 'Arrives', value: 'Immediately' },
              ]}
            />
          </div>

          <button
            type="button"
            className="tm-btn tm-btn--block"
            disabled={submitMutation.pending}
            onClick={() => {
              setSubmitError(null);
              void submitMutation.run().then((outcome) => {
                if (outcome.ok) {
                  setResult(outcome.value);
                  setCompletedAt(nowIso());
                  setStep('result');
                } else if (outcome.error) {
                  setSubmitError(
                    isMockApiError(outcome.error)
                      ? outcome.error.message
                      : 'We could not make that transfer.',
                  );
                }
              });
            }}
          >
            {submitMutation.pending ? 'Moving…' : 'Confirm transfer'}
          </button>
          <button
            type="button"
            className="tm-btn tm-btn--secondary tm-btn--block"
            onClick={() => setStep('details')}
          >
            Change details
          </button>
        </div>
      ) : null}

      {/* ---------------------------------------------------- result --- */}
      {step === 'result' && result ? (
        <div className="tm-stack">
          <div className="tm-card" style={{ textAlign: 'center' }} role="status">
            <ResultMedallion tone="success" />
            <p className="tm-eyebrow">Money moved</p>
            <Amount minorUnits={result.amount} currency={result.currency} size="hero" />
            <p className="tm-muted" style={{ margin: '6px 0 12px' }}>
              {result.from.displayName} → {result.to.displayName}
            </p>
            <StatusBadge status="completed" />
          </div>

          {/* The receipt: everything the medallion card is too terse to hold —
              which accounts moved the money, and when. */}
          <section aria-labelledby="tfr-receipt" className="tm-card">
            <SectionHeader title="Receipt" id="tfr-receipt" />
            <div style={{ marginBlockStart: 12 }}>
              <DetailList
                items={[
                  { label: 'From', value: result.from.displayName },
                  { label: 'To', value: result.to.displayName },
                  {
                    label: 'Amount',
                    value: <Amount minorUnits={result.amount} currency={result.currency} />,
                  },
                  { label: 'Reference', value: result.reference },
                  ...(completedAt ? [{ label: 'Date', value: formatDateTime(completedAt) }] : []),
                  { label: 'Status', value: <StatusBadge status="completed" /> },
                  {
                    label: `${result.from.displayName} now`,
                    value: (
                      <Amount
                        minorUnits={result.from.availableBalance}
                        currency={result.from.currency}
                      />
                    ),
                  },
                  {
                    label: `${result.to.displayName} now`,
                    value: (
                      <Amount minorUnits={result.to.availableBalance} currency={result.to.currency} />
                    ),
                  },
                ]}
              />
            </div>
          </section>

          <Link to="/mobile/accounts" className="tm-btn tm-btn--block">
            View accounts
          </Link>
          <Link to="/mobile/home" className="tm-btn tm-btn--secondary tm-btn--block">
            Back to home
          </Link>
        </div>
      ) : null}

      {step !== 'source' && step !== 'result' ? (
        <button
          type="button"
          className="tm-btn tm-btn--quiet"
          style={{ marginBlockStart: 12 }}
          onClick={() =>
            setStep(step === 'destination' ? 'source' : step === 'details' ? 'destination' : 'details')
          }
        >
          Back a step
        </button>
      ) : null}
    </>
  );
}
